<template>
  <div class='edit-service-provider-freelancer-profile-form'>
    <v-form ref="form">
      <v-text-field
        v-model="serviceProviderFreelancerProfileDetails.user.firstName"
        class="left-input"
        :rules='[rules.required]'
        @change='formChanged'
        :label="$t('registration.client.info.firstname')"
        :placeholder="$t('registration.client.info.firstname.placeholder')"
        dense
        outlined
        required
        autofocus
      />
      <v-text-field
        v-model="serviceProviderFreelancerProfileDetails.user.lastName"
        :rules='[rules.required]'
        :label="$t('registration.client.info.lastname')"
        @change='formChanged'
        :placeholder="$t('registration.client.info.lastname.placeholder')"
        dense
        outlined
        required
      />
      <vue-tel-input-vuetify
        v-model="serviceProviderFreelancerProfileDetails.user.phone"
        mode="international"
        @input='formChanged'
        :placeholder="this.$t('registration.client.info.phonenumber.placeholder')"
        :label="$t('registration.client.info.phonenumber')"
        :selectLabel="$t('registration.client.info.phonenumber.country')"
        outlined
        dense
        required
      />
      <div class='tel-error-message' v-if='!phoneNumberValid'> {{ $t('registration.client.info.phone.number.is.not.valid') }} </div>
      <v-text-field
        @change='formChanged'
        v-model="serviceProviderFreelancerProfileDetails.workerProfile.jobTitle"
        :label="$t('registration.client.info.jobtitle')"
        :placeholder="$t('registration.client.info.jobtitle.placeholder')"
        dense
        outlined
        required
      />
      <v-text-field
        @change='formChanged'
        v-model="serviceProviderFreelancerProfileDetails.workerProfile.experience"
        :rules='[rules.isNumber]'
        :label="$t('sp.registration.company.services.years.of.experience')"
        :placeholder="$t('sp.registration.company.services.years.of.experience.placeholder')"
        dense
        outlined
        required
      />
      <div class="company-services-sub-title">
        {{ $t('sp.registration.company.services.select.services') }}
      </div>
      <v-list
        flat
        class="services-list"
      >
        <v-list-group
          v-for="service in topLevelServicesWithSubs"
          :key="service"
          :append-icon="''"
        >
          <template v-slot:activator>
            <v-list-item-icon>
              <v-icon color="#E7E9EC" small>add</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title v-text="$t(`${service}`)"></v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item
            v-for="subService in formattedServicesOptions[service]"
            :key="subService"
          >
            <template>
              <v-list-item-action>
                <custom-checkbox
                  color="cold-marine"
                  size="s"
                  :value="flatServicesOptionsObject[subService]"
                  @input="onCheck(subService)"
                ></custom-checkbox>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title v-text="$t(`${subService}`)"></v-list-item-title>
              </v-list-item-content>
            </template>
          </v-list-item>
        </v-list-group>
        <v-list-item
          v-for="service in topLevelServicesWithoutSubs"
          :key="service"
        >
          <template>
            <v-list-item-action>
              <custom-checkbox
                :value="flatServicesOptionsObject[service]"
                color="cold-marine"
                @input="onCheck(service)"
                size="s"
              ></custom-checkbox>
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title v-text="$t(`${service}`)"></v-list-item-title>
            </v-list-item-content>
          </template>
        </v-list-item>
      </v-list>
      <v-text-field
        dense
        outlined
        required
        @change='formChanged'
        :rules=[rules.linkedin]
        v-model="serviceProviderFreelancerProfileDetails.workerProfile.linkedinLink"
        :label="$t('registration.sp.freelancer.company.certification.linkedin.label')"
        :placeholder="$t('registration.sp.freelancer.company.certification.linkedin.placeholder')"
      />
      <v-text-field
        dense
        outlined
        required
        @change='formChanged'
        :rules=[rules.anyWebsite]
        v-model="serviceProviderFreelancerProfileDetails.workerProfile.otherSocialMediaLink"
        :label="$t('registration.sp.freelancer.company.certification.other.social.network.label')"
        :placeholder="$t('registration.sp.freelancer.company.certification.other.social.network.placeholder')"
      />
      <v-text-field
        dense
        outlined
        required
        @change='formChanged'
        v-model="serviceProviderFreelancerProfileDetails.workerProfile.whitelistIP"
        :rules='[rules.hostWithRange]'
        :label="$t('registration.sp.freelancer.company.certification.white.ip.label')"
        :placeholder="$t('registration.sp.freelancer.company.certification.white.ip.placeholder')"
      />
      <div class="add-recommendations-title">
        {{ $t('registration.sp.freelancer.company.certification.recommendation.title') }}
      </div>
      <div class="add-recommendations-sub-title">
        {{ $t('registration.sp.freelancer.company.certification.recommendation.sub.title') }}
      </div>
      <div class="add-recommendations-form">
        <div class="emails-inputs" v-for="(value, index) in recommendations" :key="index">
          <div class="text-field-section">
            <v-text-field
              dense
              outlined
              required
              :ref="index"
              @change='formChanged'
              :rules='[rules.email]'
              v-model="recommendations[index]"
              :label="$t('registration.sp.freelancer.company.certification.recommendation.label')"
              :placeholder="$t('registration.sp.freelancer.company.certification.recommendation.placeholder')"
            />
          </div>
          <span
            v-if='isDeletable(index)'
            class="delete-icon material-icons"
            @click="deleteRecommendation(index)"
          >
            highlight_off
          </span>
        </div>
        <div class="add-new-email-section">
          <div class="add-new-email-header">
            <label  class="add-new-email-add-button" @click="addRecommendation">
              <v-icon color="#4D9FEB">add</v-icon>
              {{ $t('registration.sp.freelancer.company.certification.recommendation.add') }}
            </label>
          </div>
        </div>
      </div>
    </v-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { rules } from '@/utils/validationUtils';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';

export default {
  name: 'EditServiceProviderFreelancerProfileForm',
  components: {
    CustomCheckbox,
  },
  created() {
    if (this.rules.phone(this.serviceProviderFreelancerProfileDetails.user.phone) === true) {
      this.phoneNumberValid = true;
    }
    if (this.serviceProviderFreelancerProfileDetails?.workerProfile?.recommendationsContacts) {
      this.recommendations = this.serviceProviderFreelancerProfileDetails.workerProfile.recommendationsContacts;
    }
  },
  data() {
    return {
      phoneNumberValid: false,
      valid: true,
      recommendations: ['', ''],
    };
  },
  computed: {
    ...mapGetters([
      'serviceProviderFreelancerProfileDetails',
      'serviceProviderFreelancerRecommendationsContacts',
      'serviceProviderFreelancerServices',
      'spFreelancerServices',
    ]),
    rules() {
      return {
        ...rules,
        // eslint-disable-next-line no-restricted-globals
        isNumber: v => (!isNaN(v) && v >= 0) || this.$t('only.numbers'),
      };
    },
    topLevelServicesWithoutSubs() {
      return Object.keys(this.formattedServicesOptions).filter(service => !this.formattedServicesOptions[service].length);
    },
    topLevelServicesWithSubs() {
      return Object.keys(this.formattedServicesOptions).filter(service => this.formattedServicesOptions[service].length);
    },
    recommendationsContacts() {
      return this.serviceProviderFreelancerProfileDetails.workerProfile?.recommendationsContacts?.map(item => (item || '')) || ['', ''];
    },
    formattedServicesOptions() {
      const servicesMap = new Map();
      this.spFreelancerServices.forEach(service => {
        if (service.shortName.includes('.sub.')) {
          const topLevelService = service.shortName.split('.sub.')[0];
          if (!servicesMap.has(topLevelService)) {
            servicesMap.set(topLevelService, [service.shortName]);
          } else {
            const currentSubServices = servicesMap.get(topLevelService);
            currentSubServices.push(service.shortName);
            servicesMap.set(topLevelService, currentSubServices);
          }
        } else if (!servicesMap.has(service.shortName)) {
          servicesMap.set(service.shortName, []);
        }
      });
      return this.mapToObj(servicesMap);
    },
    flatServicesOptionsObject() {
      const flatServicesObject = {};
      this.topLevelServicesWithoutSubs.forEach(
        service => {
          flatServicesObject[service] = this.serviceProviderFreelancerServices.includes(service);
        },
      );
      this.topLevelServicesWithSubs.forEach(
        service => {
          this.formattedServicesOptions[service].forEach(
            subService => {
              flatServicesObject[subService] = this.serviceProviderFreelancerServices.includes(subService);
            },
          );
        },
      );
      return flatServicesObject;
    },
  },
  methods: {
    formChanged() {
      this.valid = this.$refs.form.inputs.every(input => input.valid) && this.phoneNumberValid;
      this.$emit('formChanged', this.valid);
    },
    onCheck(service) {
      let chosenFullService = {};
      this.spFreelancerServices.forEach(serviceOption => {
        if (serviceOption.shortName === service) {
          chosenFullService = serviceOption;
        }
      });
      let isInServices = false;
      let indexOfService = -1;

      for (let index = 0; index < this.serviceProviderFreelancerProfileDetails.workerProfile.services.length; index += 1) {
        const serviceItem = this.serviceProviderFreelancerProfileDetails.workerProfile.services[index];
        if (serviceItem.shortName === chosenFullService.shortName) {
          isInServices = true;
          indexOfService = index;
          break;
        }
      }
      if (!isInServices) {
        this.serviceProviderFreelancerProfileDetails.workerProfile.services.push(chosenFullService);
      } else {
        this.serviceProviderFreelancerProfileDetails.workerProfile.services.splice(indexOfService, 1);
      }
      this.formChanged();
    },
    mapToObj(map) {
      const obj = {};
      map.forEach((value, key) => {
        obj[key] = value;
      });
      return obj;
    },
    isDeletable(index) {
      return !(index === 1 || index === 0);
    },
    deleteRecommendation(index) {
      this.recommendations.splice(index, 1);
    },
    addRecommendation() {
      try {
        this.recommendations.push('');
        this.formChanged();
      } catch (e) {
        console.log(e);
      }
    },
  },
  watch: {
    recommendations: {
      handler() {
        this.serviceProviderFreelancerProfileDetails.workerProfile.recommendationsContacts = this.recommendations;
        this.formChanged();
      },
      deep: true,
    },
    'serviceProviderFreelancerProfileDetails.user.phone': {
      handler() {
        this.phoneNumberValid = this.rules.phone(this.serviceProviderFreelancerProfileDetails.user.phone) === true;
      },
      deep: true,
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';
.add-recommendations-title {
  height: rem(36);
  color: var(--text-primary);
  font-family: $font-family-regular;
  font-size: rem(14);
  letter-spacing: 0;
  line-height: rem(18);
  margin-bottom: rem(8);
  margin-top: rem(15);
}

.add-recommendations-sub-title{
  height: rem(36);
  color: var(--text-tertiary);
  font-family: $font-family-regular;
  font-size: rem(14);
  letter-spacing: 0;
  line-height: rem(18);
  margin-bottom: rem(30);
}
.v-text-field__details {
  display: none;
}
.add-recommendations-form {
  margin-bottom: rem(32);
  .emails-inputs {
    display: flex;
    align-items: center;
    .text-field-section{
      flex: 0 0 rem(444);
    }
    .delete-row-section{
      flex:1;
    }
    .delete-icon {
      color: var(--status-error);
      cursor: pointer;
      font-size: rem(16);
      margin-left: rem(5);
      margin-bottom: rem(5);
    }

    .dummy-icon {
      width: rem(16);
      height: rem(16);
    }
  }
  .add-new-email-section {
    padding: 0;

    .add-new-email-header {
      display: flex;
      justify-content: space-between;

      .add-new-email-add-button {
        padding-top: rem(15);
        height: rem(16);
        color: var(--brand-primary);
        font-family: $font-family-medium;
        font-size: rem(13);
        font-weight: 500;
        letter-spacing: rem(1.07);
        line-height: rem(16);
        text-align: center;
        &:hover {
          cursor: pointer;
        }
        .plus {
          font-size: rem(17);
        }
      }
    }
  }
}
.company-services-sub-title {
  margin-top: rem(20);
}
</style>
<style lang='scss' scoped>
@import '~@/assets/styles/design-system';
.tel-error-message {
  background: transparent !important;
  text-transform: capitalize;
  color: var(--status-error);
  font-size: rem(10);
  font-weight: 600;
  letter-spacing: rem(1.12);
  line-height: rem(16);
  margin-bottom: rem(16);
}

</style>
